import React from 'react';
import { CButton } from '@coreui/react-pro';
import { TrayIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import './EmptyState.scss';

/*
 * EmptyState — Module UI Guide §15. What is missing, why, and one clear next
 * action. For "no results for this filter", pass actionLabel="Clear filters".
 *
 * Usage:
 *   <EmptyState title="No data sources yet"
 *               message="Connect one and events start landing within minutes."
 *               actionLabel="Add data source" onAction={openCreate} />
 */
const EmptyState = ({
	icon,
	title,
	message,
	actionLabel,
	onAction,
	className = '',
}) => (
	<div className={`ivs-empty ${className}`.trim()}>
		<div className="ivs-empty__icon" aria-hidden="true">
			{icon || <TrayIcon size={28} weight="bold" />}
		</div>
		{title && <div className="ivs-empty__title">{title}</div>}
		{message && <p className="ivs-empty__msg">{message}</p>}
		{actionLabel && onAction && (
			<CButton className="primary-btn ivs-empty__action" onClick={onAction}>
				{actionLabel}
			</CButton>
		)}
	</div>
);

/*
 * TableEmptyState — the empty row content shared by ServerTable and Table.
 *  - A search/filter is active  → "No results for …" + "Clear search".
 *  - Otherwise                  → the page's own `emptyState`
 *                                 ({ title, message, icon, actionLabel, onAction }).
 * `emptyText` is the legacy plain-string prop; it becomes the title.
 */
export const TableEmptyState = ({ emptyState, emptyText, search, onClearSearch }) => {
	const term = typeof search === 'string' ? search.trim() : '';
	if (term) {
		return (
			<EmptyState
				icon={<MagnifyingGlassIcon size={28} weight="bold" />}
				title="No results found"
				message={`Nothing matches “${term}”. Check the spelling or try a different search.`}
				actionLabel={onClearSearch ? 'Clear search' : undefined}
				onAction={onClearSearch}
			/>
		);
	}
	const config = emptyState || {};
	return (
		<EmptyState
			icon={config.icon}
			title={config.title || emptyText || 'Nothing here yet'}
			message={config.message}
			actionLabel={config.actionLabel}
			onAction={config.onAction}
		/>
	);
};

export default EmptyState;
